/**
 * Visits – Open and close patient visits. Each visit produces one consolidated invoice.
 */

import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { patientsApi, visitsApi } from '@/services/api';
import type { Patient, Visit } from '@/types/index.ts';
import { handleApiError } from '@/utils/errorHandler';

type TabView = 'open' | 'all';

// ─── Confirmation Dialog ──────────────────────────────────────────────────────

interface ConfirmDialogProps {
  visit: Visit;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function CloseConfirmDialog({ visit, onConfirm, onCancel, isLoading }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative bg-white rounded shadow-xl w-full max-w-sm border border-gray-200 overflow-hidden">
        {/* Top accent */}
        <div className="h-1 bg-red-500 w-full" />

        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-door-closed-line text-red-600 text-lg" aria-hidden />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Close Visit</h3>
              <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                The invoice will be finalized. No further medications can be issued to the patient.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-60 transition-colors flex items-center gap-1.5"
            >
              {isLoading && <i className="ri-loader-4-line animate-spin" aria-hidden />}
              {isLoading ? 'Closing…' : 'Close Visit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReopenConfirmDialog({ visit, onConfirm, onCancel, isLoading }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded shadow-xl w-full max-w-sm border border-gray-200 overflow-hidden">
        <div className="h-1 bg-[#0997a4] w-full" />
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#0997a4]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-door-open-line text-[#0997a4] text-lg" aria-hidden />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Reopen Visit</h3>
              <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                The invoice will return to open status and medications can be added again to the patient.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[#0997a4] rounded hover:bg-[#087d89] disabled:opacity-60 transition-colors flex items-center gap-1.5"
            >
              {isLoading && <i className="ri-loader-4-line animate-spin" aria-hidden />}
              {isLoading ? 'Reopening…' : 'Reopen Visit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function VisitsPage() {
  const [tab, setTab] = useState<TabView>('open');

  // Current user role
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('userData') ?? 'null'); } catch { return null; }
  })();
  const isAdmin = currentUser?.role === 'admin';

  // Open visit form state
  const [showNewVisitForm, setShowNewVisitForm] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [showNewPatientFields, setShowNewPatientFields] = useState(false);
  const [newPatientName, setNewPatientName] = useState('');
  const [newPatientMrn, setNewPatientMrn] = useState('');
  const [newPatientDob, setNewPatientDob] = useState('');
  const [visitType, setVisitType] = useState<'outpatient' | 'inpatient'>('outpatient');
  const [visitNotes, setVisitNotes] = useState('');
  const [isSearchingPatient, setIsSearchingPatient] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  // Visit list state
  const [visits, setVisits] = useState<Visit[]>([]);
  const [isLoadingVisits, setIsLoadingVisits] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalVisits, setTotalVisits] = useState(0);
  const PER_PAGE = 15;

  // Confirmation dialogs
  const [closeTarget, setCloseTarget] = useState<Visit | null>(null);
  const [reopenTarget, setReopenTarget] = useState<Visit | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadVisits = useCallback(async (page = 1) => {
    setIsLoadingVisits(true);
    setListError(null);
    try {
      const params: { status?: 'open' | 'closed'; per_page: number; page: number } = { per_page: PER_PAGE, page };
      if (tab === 'open') params.status = 'open';
      const res = (await visitsApi.getVisits(params)) as unknown as {
        data?: Visit[] | { data?: Visit[]; meta?: { current_page: number; last_page: number; total: number } };
        meta?: { current_page: number; last_page: number; total: number };
      };
      // Handle both flat array and paginated object shapes
      const raw = res?.data;
      if (Array.isArray(raw)) {
        setVisits(raw);
        setLastPage(1);
        setTotalVisits(raw.length);
      } else if (raw && typeof raw === 'object' && 'data' in raw) {
        const paginated = raw as { data?: Visit[]; meta?: { current_page: number; last_page: number; total: number } };
        setVisits(paginated.data ?? []);
        setCurrentPage(paginated.meta?.current_page ?? page);
        setLastPage(paginated.meta?.last_page ?? 1);
        setTotalVisits(paginated.meta?.total ?? 0);
      } else {
        // top-level meta (BaseApiController paginated shape)
        const topMeta = (res as any)?.meta;
        setVisits((raw as any) ?? []);
        setCurrentPage(topMeta?.current_page ?? page);
        setLastPage(topMeta?.last_page ?? 1);
        setTotalVisits(topMeta?.total ?? 0);
      }
    } catch (err) {
      setListError(handleApiError(err, 'Visits').message);
    } finally {
      setIsLoadingVisits(false);
    }
  }, [tab]);

  // Reset to page 1 when tab changes
  useEffect(() => { setCurrentPage(1); }, [tab]);
  useEffect(() => { loadVisits(currentPage); }, [loadVisits, currentPage]);

  // Auto-dismiss success after 4 s
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Auto-dismiss form error toast after 5 s
  useEffect(() => {
    if (!formError) return;
    const t = setTimeout(() => setFormError(null), 5000);
    return () => clearTimeout(t);
  }, [formError]);

  const resetNewVisitForm = () => {
    setPatientSearch('');
    setPatientResults([]);
    setSelectedPatient(null);
    setShowNewPatientFields(false);
    setNewPatientName('');
    setNewPatientMrn('');
    setNewPatientDob('');
    setVisitType('outpatient');
    setVisitNotes('');
    setFormError(null);
  };

  const handlePatientSearch = async () => {
    const q = patientSearch.trim();
    if (!q) return;
    setIsSearchingPatient(true);
    setPatientResults([]);
    setSelectedPatient(null);
    setShowNewPatientFields(false);
    try {
      const res = (await patientsApi.getPatients({ search: q, per_page: 20 })) as unknown as { data?: Patient[] | { data?: Patient[] } };
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && 'data' in raw ? (raw as { data?: Patient[] }).data : null) ?? [];
      setPatientResults(list);
    } catch (err) {
      setFormError(handleApiError(err, 'Patient search').message);
    } finally {
      setIsSearchingPatient(false);
    }
  };

  const handleOpenVisit = async () => {
    setFormError(null);

    let patient = selectedPatient;

    // If creating a new patient inline
    if (showNewPatientFields) {
      if (!newPatientName.trim()) {
        setFormError('Please enter the patient name.');
        return;
      }
      if (!newPatientDob) {
        setFormError('Date of birth is required.');
        return;
      }
      setIsOpening(true);
      try {
        const res = await patientsApi.findOrCreatePatient({
          name: newPatientName.trim(),
          identifier: newPatientMrn.trim() || undefined,
          date_of_birth: newPatientDob,
        });
        patient = (res as any).data ?? res;
      } catch (err) {
        setFormError(handleApiError(err, 'Create patient').message);
        setIsOpening(false);
        return;
      }
    }

    if (!patient) {
      setFormError('Please select or create a patient first.');
      return;
    }

    setIsOpening(true);
    try {
      await visitsApi.createVisit({
        patient_id: patient.id,
        visit_type: visitType,
        notes: visitNotes || undefined,
      });
      setSuccessMsg(`Visit opened for ${patient.name}.`);
      setShowNewVisitForm(false);
      resetNewVisitForm();
      if (tab === 'open') loadVisits(1);
    } catch (err) {
      setFormError(handleApiError(err, 'Open visit').message);
    } finally {
      setIsOpening(false);
    }
  };

  const handleConfirmClose = async () => {
    if (!closeTarget) return;
    setIsClosing(true);
    try {
      await visitsApi.closeVisit(closeTarget.id);
      setSuccessMsg(`Visit ${closeTarget.reference_number} closed.`);
      setCloseTarget(null);
      loadVisits(currentPage);
    } catch (err) {
      setListError(handleApiError(err, 'Close visit').message);
      setCloseTarget(null);
    } finally {
      setIsClosing(false);
    }
  };

  const handleConfirmReopen = async () => {
    if (!reopenTarget) return;
    setIsReopening(true);
    try {
      await visitsApi.reopenVisit(reopenTarget.id);
      setSuccessMsg(`Visit ${reopenTarget.reference_number} reopened.`);
      setReopenTarget(null);
      loadVisits(currentPage);
    } catch (err) {
      setListError(handleApiError(err, 'Reopen visit').message);
      setReopenTarget(null);
    } finally {
      setIsReopening(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Close confirmation dialog */}
      {closeTarget && (
        <CloseConfirmDialog
          visit={closeTarget}
          onConfirm={handleConfirmClose}
          onCancel={() => setCloseTarget(null)}
          isLoading={isClosing}
        />
      )}

      {/* Reopen confirmation dialog */}
      {reopenTarget && (
        <ReopenConfirmDialog
          visit={reopenTarget}
          onConfirm={handleConfirmReopen}
          onCancel={() => setReopenTarget(null)}
          isLoading={isReopening}
        />
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Visits</h1>
            <p className="text-sm text-gray-600 mt-1">
              Open a visit for a patient before issuing medications.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setShowNewVisitForm(true); setFormError(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#0997a4] text-white rounded-lg text-sm font-medium hover:bg-[#087d89] transition-colors"
          >
            <i className="ri-add-line" aria-hidden />
            Open New Visit
          </button>
        </div>

        {/* Error toast (open visit form) */}
        {formError && (
          <div className="fixed top-4 right-4 z-50 min-w-[300px] max-w-md shadow-lg flex rounded-sm overflow-hidden border border-red-200">
            <div className="w-1 bg-red-500 flex-shrink-0" />
            <div className="flex items-start gap-2.5 flex-1 bg-red-50 px-3 py-2.5">
              <i className="ri-error-warning-fill text-red-500 text-sm mt-0.5 flex-shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-900">Error</p>
                <p className="text-xs text-red-700 mt-0.5">{formError}</p>
              </div>
              <button type="button" onClick={() => setFormError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                <i className="ri-close-line text-sm" aria-hidden />
              </button>
            </div>
          </div>
        )}

        {/* Success banner */}
        {successMsg && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-green-800 text-sm">
              <i className="ri-checkbox-circle-line text-green-600" aria-hidden />
              {successMsg}
            </div>
            <button type="button" onClick={() => setSuccessMsg(null)} className="text-green-600 hover:text-green-800">
              <i className="ri-close-line" aria-hidden />
            </button>
          </div>
        )}

        {/* New Visit Form */}
        {showNewVisitForm && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Open a New Visit</h2>
              <button
                type="button"
                onClick={() => { setShowNewVisitForm(false); resetNewVisitForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl" aria-hidden />
              </button>
            </div>

            {/* Patient search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Patient <span className="text-red-500">*</span>
              </label>

              {!selectedPatient && !showNewPatientFields && (
                <>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handlePatientSearch()}
                      placeholder="Search by name or MRN"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4]"
                    />
                    <button
                      type="button"
                      onClick={handlePatientSearch}
                      disabled={isSearchingPatient || !patientSearch.trim()}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition-colors"
                    >
                      {isSearchingPatient ? (
                        <i className="ri-loader-4-line animate-spin" aria-hidden />
                      ) : 'Search'}
                    </button>
                  </div>

                  {/* Search results */}
                  {patientResults.length > 0 && (
                    <ul className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                      {patientResults.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => { setSelectedPatient(p); setPatientResults([]); }}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex justify-between items-center"
                          >
                            <span className="font-medium text-gray-900">{p.name}</span>
                            {p.identifier && <span className="text-gray-400 text-xs"> {p.identifier}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* No results — offer to create */}
                  {patientResults.length === 0 && patientSearch.trim() && !isSearchingPatient && (
                    <div className="mt-2 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                      <p className="text-xs text-gray-500">No patient found for "{patientSearch}"</p>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewPatientFields(true);
                          setNewPatientName(patientSearch.trim());
                          setPatientResults([]);
                        }}
                        className="text-xs font-medium text-[#0997a4] hover:underline ml-3 whitespace-nowrap"
                      >
                        + Create new patient
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* New patient inline form */}
              {showNewPatientFields && !selectedPatient && (
                <div className="border border-[#0997a4]/30 rounded-lg p-4 bg-[#0997a4]/5 space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-[#0997a4] uppercase tracking-wide">New Patient</p>
                    <button
                      type="button"
                      onClick={() => { setShowNewPatientFields(false); setNewPatientName(''); setNewPatientMrn(''); setNewPatientDob(''); }}
                      className="text-gray-400 hover:text-gray-600 text-xs"
                    >
                      <i className="ri-close-line" aria-hidden /> Back to search
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newPatientName}
                      onChange={(e) => setNewPatientName(e.target.value)}
                      placeholder="e.g. Jane Smith"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={newPatientDob}
                      onChange={(e) => setNewPatientDob(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      MRN <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={newPatientMrn}
                      onChange={(e) => setNewPatientMrn(e.target.value)}
                      placeholder="e.g. MRN-00123"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4]"
                    />
                  </div>
                </div>
              )}

              {/* Selected patient chip */}
              {selectedPatient && (
                <div className="mt-1 flex items-center gap-2 bg-[#0997a4]/10 border border-[#0997a4]/30 rounded-lg px-3 py-2 text-sm">
                  <i className="ri-user-line text-[#0997a4]" aria-hidden />
                  <span className="font-medium text-[#0997a4]">{selectedPatient.name}</span>
                  {selectedPatient.identifier && (
                    <span className="text-gray-500 text-xs">· MRN: {selectedPatient.identifier}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => { setSelectedPatient(null); setPatientSearch(''); }}
                    className="ml-auto text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-line" aria-hidden />
                  </button>
                </div>
              )}
            </div>

            {/* Visit type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Visit Type</label>
              <div className="flex gap-4">
                {(['outpatient', 'inpatient'] as const).map((vt) => (
                  <label key={vt} className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border text-sm transition-colors ${visitType === vt ? 'border-[#0997a4] bg-[#0997a4]/5 text-[#0997a4] font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="visit_type"
                      value={vt}
                      checked={visitType === vt}
                      onChange={() => setVisitType(vt)}
                      className="accent-[#0997a4]"
                    />
                    <span className="capitalize">{vt}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea
                value={visitNotes}
                onChange={(e) => setVisitNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4] resize-none"
                placeholder="e.g. Admitted for post-op care"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setShowNewVisitForm(false); resetNewVisitForm(); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOpenVisit}
                disabled={isOpening || (!selectedPatient && !showNewPatientFields)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#0997a4] rounded-lg hover:bg-[#087d89] disabled:opacity-60 transition-colors flex items-center gap-2"
              >
                {isOpening && <i className="ri-loader-4-line animate-spin" aria-hidden />}
                {isOpening ? 'Opening…' : 'Open Visit'}
              </button>
            </div>
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex gap-1 border-b border-gray-200">
          {([['open', 'Open Visits'], ['all', 'All Visits']] as [TabView, string][]).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value as TabView)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === value
                  ? 'border-[#0997a4] text-[#0997a4]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* List error */}
        {listError && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <i className="ri-error-warning-line text-amber-600 text-xl flex-shrink-0 mt-0.5" aria-hidden />
            <p className="text-sm text-amber-800">{listError}</p>
            <button type="button" onClick={() => setListError(null)} className="text-amber-600 hover:text-amber-800 ml-auto">
              <i className="ri-close-line" aria-hidden />
            </button>
          </div>
        )}

        {/* Visit list */}
        {isLoadingVisits ? (
          <div className="py-12 text-center text-gray-500">
            <i className="ri-loader-4-line animate-spin text-2xl" aria-hidden />
            <p className="mt-2 text-sm">Loading visits…</p>
          </div>
        ) : visits.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <i className="ri-calendar-check-line text-4xl" aria-hidden />
            <p className="mt-2 text-sm">
              {tab === 'open' ? 'No open visits right now.' : 'No visits found.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">MRN</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Patient</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Opened</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visits.map((visit) => {
                  const isOpen = visit.status === 'open';
                  const dateOpened = visit.created_at
                    ? new Date(visit.created_at).toLocaleString()
                    : '—';
                  return (
                    <tr key={visit.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-gray-700">
                        {visit.patient?.identifier ?? <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{visit.patient?.name ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3 capitalize text-gray-600">{visit.visit_type}</td>
                      <td className="px-4 py-3 text-gray-500">{dateOpened}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-green-500' : 'bg-gray-400'}`} />
                          {isOpen ? 'Open' : 'Closed'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isOpen && (
                          <button
                            type="button"
                            onClick={() => setCloseTarget(visit)}
                            className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-300 hover:bg-red-50 rounded transition-colors"
                          >
                            Close Visit
                          </button>
                        )}
                        {!isOpen && (
                          <div className="flex items-center justify-end gap-3">
                            <span className="text-xs text-gray-400">
                              {visit.closed_at
                                ? new Date(visit.closed_at).toLocaleDateString()
                                : 'Closed'}
                            </span>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => setReopenTarget(visit)}
                                className="px-3 py-1.5 text-xs font-medium text-[#0997a4] hover:bg-[#0997a4]/10 rounded-lg transition-colors border border-[#0997a4]/30"
                              >
                                Reopen
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="flex items-center justify-between gap-4 pt-1">
            <p className="text-xs text-gray-500">
              Page {currentPage} of {lastPage} &middot; {totalVisits} visit{totalVisits !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || isLoadingVisits}
                className="px-2 py-1.5 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="First page"
              >
                <i className="ri-skip-back-line" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1 || isLoadingVisits}
                className="px-2 py-1.5 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Previous page"
              >
                <i className="ri-arrow-left-s-line" aria-hidden />
              </button>

              {/* Page number pills */}
              {Array.from({ length: lastPage }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === lastPage || Math.abs(p - currentPage) <= 1)
                .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === '…' ? (
                    <span key={`ellipsis-${idx}`} className="px-1.5 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setCurrentPage(p as number)}
                      disabled={isLoadingVisits}
                      className={`min-w-[30px] px-2 py-1.5 text-xs rounded border transition-colors ${
                        currentPage === p
                          ? 'bg-[#0997a4] border-[#0997a4] text-white font-medium'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(lastPage, p + 1))}
                disabled={currentPage === lastPage || isLoadingVisits}
                className="px-2 py-1.5 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Next page"
              >
                <i className="ri-arrow-right-s-line" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(lastPage)}
                disabled={currentPage === lastPage || isLoadingVisits}
                className="px-2 py-1.5 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Last page"
              >
                <i className="ri-skip-forward-line" aria-hidden />
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
